import { apiClient } from './apiClient';
import type { Post } from '../types';
import { Logger } from '../utils/logger';

interface PostFilters {
  status?: string;
  channelId?: string;
  startDate?: Date;
  endDate?: Date;
}

interface BackendPost {
  id: string;
  content: string;
  channel_id: string;
  status: string;
  generated_date?: string;
  scheduled_date?: string;
  published_date?: string;
  image_url?: string;
  preview?: string;
  is_newsletter?: boolean;
  newsletter_data?: unknown;
  source_ids?: string[];
  source_urls?: string[];
  source_references?: Array<{
    source_id?: string;
    id?: string;
    title?: string;
    url?: string;
    source_type?: string;
    type?: string;
    relevance_score?: number;
    relevance?: number;
    key_facts?: string[];
    facts?: string[];
    ai_summary?: string;
    post_angles?: string[];
    keywords?: string[];
    entity_names?: string[];
  }>;
  source_inspiration?: string;
  generation_angle?: string;
  hashtags?: string[];
  platform?: string;
  compliance_scores?: unknown;
  zernio_post_id?: string;
  published_url?: string;
  media_items?: Array<{
    type: string;
    url: string;
    thumbnail_url?: string;
    alt_text?: string;
  }>;
  video_url?: string;
  platform_specific_data?: unknown;
  custom_content?: unknown;
  error_message?: string;
  publish_attempts?: number;
  media_type?: string;
  carousel_id?: string;
  // V4-POSTS-CREATED-BY-USER-ID 2026-05-28
  created_by_user_id?: string;
  // V4-KNOWLEDGE-FALLBACK-UX 2026-05-29
  knowledge_source_quality?: string;
}

type BackendPostRecord = Record<string, unknown>;

export const postsService = {
  getAll: async (spaceId: string, filters?: PostFilters): Promise<Post[]> => {
    Logger.debug('[PostsService] Fetching posts', {
      spaceId,
      hasStatus: !!filters?.status,
      hasChannelId: !!filters?.channelId,
      hasStartDate: !!filters?.startDate,
      hasEndDate: !!filters?.endDate,
    });
    const params: Record<string, string> = { space_id: spaceId };
    if (filters?.status) params.status = filters.status;
    if (filters?.channelId) params.channel_id = filters.channelId;
    if (filters?.startDate) params.start_date = filters.startDate.toISOString();
    if (filters?.endDate) params.end_date = filters.endDate.toISOString();

    const response = await apiClient.get<BackendPost[]>('/posts/', { params });
    const posts = response.data.map(mapBackendPostToFrontend);
    Logger.info('[PostsService] Posts fetched', { spaceId, count: posts.length });
    return posts;
  },

  create: async (spaceId: string, post: Partial<Post>) => {
    Logger.info('[PostsService] Creating post', {
      spaceId,
      channelId: post.channelId,
      hasContent: !!post.content,
      hasImage: !!post.imageUrl,
      hasMediaItems: !!post.mediaItems?.length,
    });
    const payload = mapFrontendPostToBackend(post, spaceId);
    const response = await apiClient.post<BackendPost>('/posts/', payload);
    const createdPost = mapBackendPostToFrontend(response.data);
    Logger.info('[PostsService] Post created', { postId: createdPost.id, spaceId, channelId: createdPost.channelId });
    return createdPost;
  },

  getById: async (id: string): Promise<Post> => {
    const response = await apiClient.get<BackendPost>(`/posts/${id}`);
    return mapBackendPostToFrontend(response.data);
  },

  getPublishStatus: async (id: string): Promise<{ publishedUrl: string }> => {
    const response = await apiClient.get<{ published_url: string }>(`/posts/${id}/publish-status`);
    return { publishedUrl: response.data.published_url || '' };
  },

  delete: async (id: string) => {
    Logger.warn('[PostsService] Deleting post', { postId: id });
    await apiClient.delete(`/posts/${id}`);
    Logger.info('[PostsService] Post deleted', { postId: id });
    return { success: true };
  },

  update: async (post: Partial<Post> & { id: string }, spaceId?: string) => {
    Logger.info('[PostsService] Updating post', {
      postId: post.id,
      spaceId: spaceId ?? null,
      hasContent: post.content !== undefined,
      hasChannelId: post.channelId !== undefined,
      hasScheduledDate: post.scheduledDate !== undefined,
      hasMediaItems: post.mediaItems !== undefined,
    });
    const payload = mapFrontendPostToBackend(post, spaceId);
    const response = await apiClient.put<BackendPost>(`/posts/${post.id}`, payload);
    const updatedPost = mapBackendPostToFrontend(response.data);
    Logger.info('[PostsService] Post updated', { postId: updatedPost.id, channelId: updatedPost.channelId });
    return updatedPost;
  },
  
  unschedule: async (id: string): Promise<Post> => {
    Logger.info('[PostsService] Unscheduling post', { postId: id });
    const response = await apiClient.put<BackendPost>(`/posts/${id}`, { scheduled_date: null });
    const unscheduledPost = mapBackendPostToFrontend(response.data);
    Logger.info('[PostsService] Post unscheduled', { postId: unscheduledPost.id });
    return unscheduledPost;
  },

  publishNow: async (id: string): Promise<Post> => {
    Logger.info('[PostsService] Publishing post now', { postId: id });
    const response = await apiClient.post<BackendPost>(`/posts/${id}/publish`);
    const publishedPost = mapBackendPostToFrontend(response.data);
    Logger.info('[PostsService] Post published', { postId: publishedPost.id, publishedDate: publishedPost.publishedDate?.toISOString() ?? null });
    return publishedPost;
  },

  /**
   * Relance le scoring de qualité (compliance) sur un post existant.
   *
   * Usage côté UI : bouton "Réessayer le scoring" sur la `QualityScoreCard`
   * quand le score affiché est ``SKIPPED`` (cas typique : DeserializationError
   * arq côté backend qui a sauté le scoring initial).
   *
   * Le backend rate-limite à 1 rescore / 60s / post (Redis SETNX). En cas de 429,
   * c'est au caller d'afficher un toast adapté.
   */
  rescore: async (id: string): Promise<Post> => {
    Logger.info('[PostsService] Rescoring post', { postId: id });
    const response = await apiClient.post<BackendPost>(`/posts/${id}/rescore`);
    const rescored = mapBackendPostToFrontend(response.data);
    Logger.info('[PostsService] Post rescored', { postId: rescored.id });
    return rescored;
  },
};

function mapBackendPostToFrontend(data: BackendPost): Post {
  return {
    id: data.id,
    content: data.content,
    channelId: data.channel_id,
    status: data.status,
    generatedDate: data.generated_date ? new Date(data.generated_date) : new Date(),
    scheduledDate: data.scheduled_date ? new Date(data.scheduled_date) : undefined,
    publishedDate: data.published_date ? new Date(data.published_date) : undefined,
    imageUrl: data.image_url,
    preview: data.preview,
    isNewsletter: data.is_newsletter,
    newsletterData: data.newsletter_data,
    // Source traceability fields - map backend snake_case to frontend camelCase
    sourceIds: data.source_ids,
    sourceUrls: data.source_urls,
    // V4-FRONT-ANY-USAGE 2026-05-28 — typed from BackendPost.source_references
    sourceReferences: data.source_references?.map((ref) => ({
      id: ref.source_id || ref.id,
      title: ref.title,
      url: ref.url,
      type: ref.source_type || ref.type || 'unknown',
      relevance: ref.relevance_score || ref.relevance,
      facts: ref.key_facts || ref.facts || [],
      aiSummary: ref.ai_summary || '',
      postAngles: ref.post_angles || [],
      keywords: ref.keywords || [],
      entityNames: ref.entity_names || [],
    })),
    // Generation metadata
    sourceInspiration: data.source_inspiration,
    generationAngle: data.generation_angle,
    hashtags: data.hashtags,
    platform: data.platform,
    // Quality scores from compliance agent
    complianceScores: data.compliance_scores || undefined,
    // Zernio publishing fields
    zernioPostId: data.zernio_post_id,
    publishedUrl: data.published_url,
    // V4-FRONT-ANY-USAGE 2026-05-28 — typed from BackendPost.media_items
    mediaItems: data.media_items?.map((item) => ({
      type: item.type,
      url: item.url,
      thumbnailUrl: item.thumbnail_url,
      altText: item.alt_text,
    })),
    videoUrl: data.video_url,
    platformSpecificData: data.platform_specific_data,
    customContent: data.custom_content,
    errorMessage: data.error_message,
    publishAttempts: data.publish_attempts,
    mediaType: data.media_type as Post['mediaType'],
    carouselId: data.carousel_id,
    // V4-POSTS-CREATED-BY-USER-ID 2026-05-28
    createdByUserId: data.created_by_user_id,
    // V4-KNOWLEDGE-FALLBACK-UX 2026-05-29
    knowledgeSourceQuality: data.knowledge_source_quality as Post['knowledgeSourceQuality'],
  } as Post;
}

function mapFrontendPostToBackend(post: Partial<Post>, spaceId?: string): BackendPostRecord {
  const data: BackendPostRecord = {};
  if (spaceId) data.space_id = spaceId;
  if (post.content !== undefined) data.content = post.content;
  if (post.channelId !== undefined) data.channel_id = post.channelId;
  if (post.status !== undefined) data.status = post.status;
  if (post.imageUrl !== undefined) data.image_url = post.imageUrl;
  if (post.preview !== undefined) data.preview = post.preview;
  if (post.isNewsletter !== undefined) data.is_newsletter = post.isNewsletter;
  if (post.newsletterData !== undefined) data.newsletter_data = post.newsletterData;
  
  if (post.generatedDate) data.generated_date = post.generatedDate.toISOString();
  if (post.scheduledDate) data.scheduled_date = post.scheduledDate.toISOString();

  // Zernio publishing fields
  if (post.mediaItems !== undefined) {
    data.media_items = post.mediaItems.map((item) => ({
      type: item.type,
      url: item.url,
      thumbnail_url: item.thumbnailUrl,
      alt_text: item.altText,
    }));
  }
  if (post.videoUrl !== undefined) data.video_url = post.videoUrl;
  if (post.platformSpecificData !== undefined) data.platform_specific_data = post.platformSpecificData;
  if (post.customContent !== undefined) data.custom_content = post.customContent;
  if (post.mediaType !== undefined) data.media_type = post.mediaType;
  if (post.carouselId !== undefined) data.carousel_id = post.carouselId;

  return data;
}

